/**
 * Country utility functions for working with ISO codes and country names
 */

export interface Country {
    value: string; // ISO code (e.g., 'LT', 'US')
    label: string; // Full name (e.g., 'Lithuania', 'United States')
}

/**
 * Get the full country name from an ISO code
 * @param isoCode - The 2-character ISO country code (e.g., 'LT')
 * @param countries - Array of country objects with value/label
 * @returns The full country name, or the ISO code if not found
 */
export function getCountryName(isoCode: string | null | undefined, countries: Country[]): string {
    if (!isoCode) return '';

    const country = countries.find((c) => c.value === isoCode);
    return country ? country.label : isoCode;
}

/**
 * Get the ISO code from a country name
 * @param countryName - The full country name (e.g., 'Lithuania')
 * @param countries - Array of country objects with value/label
 * @returns The ISO code, or the country name if not found
 */
export function getCountryCode(countryName: string | null | undefined, countries: Country[]): string {
    if (!countryName) return '';

    const country = countries.find((c) => c.label === countryName);
    return country ? country.value : countryName;
}

/**
 * Format a country display string with both name and ISO code
 * @param isoCode - The 2-character ISO country code (e.g., 'LT')
 * @param countries - Array of country objects with value/label
 * @returns Formatted string like "Lithuania (LT)" or just the ISO code if not found
 */
export function formatCountryDisplay(isoCode: string | null | undefined, countries: Country[]): string {
    if (!isoCode) return '';

    const country = countries.find((c) => c.value === isoCode);
    return country ? `${country.label} (${country.value})` : isoCode;
}

/**
 * Check if a string is a valid ISO country code (2 uppercase letters)
 * @param value - The value to check
 * @returns true if the value appears to be an ISO code
 */
export function isIsoCode(value: string | null | undefined): boolean {
    if (!value) return false;
    return /^[A-Z]{2}$/.test(value);
}

/**
 * Normalize a country value to ISO code
 * If it's already an ISO code, return it. If it's a full name, convert it.
 * @param value - The country value (either ISO code or full name)
 * @param countries - Array of country objects with value/label
 * @returns The ISO code
 */
export function normalizeToIsoCode(value: string | null | undefined, countries: Country[]): string {
    if (!value) return '';

    // If it's already an ISO code, return it
    if (isIsoCode(value)) return value;

    // Otherwise, try to find the ISO code from the full name
    return getCountryCode(value, countries);
}

/**
 * Normalize a country value to full name
 * If it's an ISO code, convert it. If it's already a full name, return it.
 * @param value - The country value (either ISO code or full name)
 * @param countries - Array of country objects with value/label
 * @returns The full country name
 */
export function normalizeToCountryName(value: string | null | undefined, countries: Country[]): string {
    if (!value) return '';

    // If it's an ISO code, convert it
    if (isIsoCode(value)) return getCountryName(value, countries);

    // Otherwise, assume it's already a full name
    return value;
}
